"use client";

import Link from "next/link";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HomeEventCard } from "@/components/home/cards/HomeEventCard";
import type { Event } from "@/lib/api-types";
import { cn } from "@/lib/utils";

const AUTO_INTERVAL_MS = 7_000;

type HomeEventsSlideshowProps = {
  events: Event[];
};

export function HomeEventsSlideshow({ events }: HomeEventsSlideshowProps) {
  const reduced = useReducedMotion();
  const liveRegionId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const total = events.length;

  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusedWithin, setIsFocusedWithin] = useState(false);
  const [progressKey, setProgressKey] = useState(0);

  const event = events[activeIndex];
  const autoEnabled = !reduced && !isHovered && !isFocusedWithin && total > 1;

  const goToIndex = useCallback(
    (index: number) => {
      setActiveIndex((index + total) % total);
      setProgressKey((k) => k + 1);
    },
    [total]
  );

  const advance = useCallback(
    (direction: 1 | -1) => {
      goToIndex(activeIndex + direction);
    },
    [activeIndex, goToIndex]
  );

  useEffect(() => {
    if (!autoEnabled) return;

    const timer = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % total);
      setProgressKey((k) => k + 1);
    }, AUTO_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [autoEnabled, progressKey, total]);

  const handleFocusIn = () => setIsFocusedWithin(true);
  const handleFocusOut = (e: React.FocusEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsFocusedWithin(false);
    }
  };

  if (!event) return null;

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocusCapture={handleFocusIn}
      onBlurCapture={handleFocusOut}
    >
      <div
        id={liveRegionId}
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      >
        {event.title}, event {activeIndex + 1} of {total}
      </div>

      <div className="relative h-[22rem] overflow-hidden rounded-xl sm:h-[24rem]">
        <AnimatePresence initial={false}>
          <motion.div
            key={event.id}
            initial={reduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduced ? undefined : { opacity: 0 }}
            transition={{ duration: reduced ? 0 : 0.35 }}
            className="absolute inset-0"
          >
            <Link href="/events" className="block h-full">
              <HomeEventCard event={event} variant="slideshow" className="h-full" />
            </Link>
          </motion.div>
        </AnimatePresence>
      </div>

      {total > 1 ? (
        <div className="mt-3 flex flex-col items-center gap-3">
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => advance(-1)}
              aria-label="Previous event"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-card"
            >
              <ChevronLeft size={16} aria-hidden />
            </button>

            <span className="min-w-[4.5rem] text-center text-sm font-medium text-muted">
              {activeIndex + 1} of {total}
            </span>

            <button
              type="button"
              onClick={() => advance(1)}
              aria-label="Next event"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-card"
            >
              <ChevronRight size={16} aria-hidden />
            </button>
          </div>

          <div
            className="flex flex-wrap items-center justify-center gap-2"
            role="tablist"
            aria-label="Upcoming events"
          >
            {events.map((item, index) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                aria-label={`${item.title}, event ${index + 1}`}
                onClick={() => goToIndex(index)}
                className={cn(
                  "h-2 rounded-full transition-all duration-300",
                  index === activeIndex
                    ? "w-5 bg-primary"
                    : "w-2 bg-border hover:bg-muted"
                )}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
